import React, {
  FunctionComponent,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import cloneDeep from 'lodash.clonedeep';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { connect, ConnectedProps } from 'react-redux';
import { RootState } from 'data/root.reducers';
import { CollectionFunctionGroupFormData } from 'data/collectionFunctionGroup/collectionFunctionGroup.types';
import { adaptToForm } from 'data/collectionFunctionGroup/collectionFunctionGroup.adapter';
import * as collectionFunctionGroupSelectors from 'data/collectionFunctionGroup/collectionFunctionGroup.selectors';
import * as globalSelectors from 'global/global-selectors';
import * as actions from 'data/collectionFunctionGroup/collectionFunctionGroup.actions';
import * as discoverySelectors from 'xcipio/discovery-xcipio-selectors';
import * as authenticationSelectors from 'data/authentication/authentication.selectors';
import * as securityInfoSelectors from 'data/securityInfo/securityInfo.selectors';
import * as securityInfoActions from 'data/securityInfo/securityInfo.actions';
import * as contactSelectors from 'data/contact/contact.selectors';
import * as contactActions from 'data/contact/contact.actions';
import { FieldUpdateHandlerParam } from 'shared/form';
import { FormTemplateSection } from 'data/template/template.types';
import FormDialog from 'components/FormDialog';
import FormPanel from 'components/FormPanel';
import FormButtons from 'components/FormButtons';
import CollectionFunctionGroupForm, { CollectionFunctionGroupFormRef } from '../../components/CollectionFunctionGroupForm';
import { collectionFunctionGroupTemplate } from '../../templates/collectionFunctionGroupTemplate';
import getCollectionFunctionGroupDataToSubmit from '../../utils/getCollectionFunctionGroupDataToSubmit';

type RouterParams = {
  history: string
}

const mapState = (state: RootState) => ({
  isOpen: collectionFunctionGroupSelectors.isCollectionFunctionGroupEditPopupOpen(state),
  deliveryFunctionId: globalSelectors.getSelectedDeliveryFunctionId(state),
  selectedCollectionFunctionGroup: collectionFunctionGroupSelectors.getSelectedCollectionFunctionGroup(state),
  isAuditEnabled: discoverySelectors.isDiscoverySettingAuditEnabled(state),
  userId: authenticationSelectors.getAuthenticationUserId(state),
  userName: authenticationSelectors.getAuthenticationUserName(state),
  collectionFunctionGroup: collectionFunctionGroupSelectors.getCollectionFunctionGroup(state),
  securityInfos: securityInfoSelectors.getSecurityInfos(state),
  contacts: contactSelectors.getContacts(state),
});

const mapDispatch = {
  editCollectionFunctionGroup: actions.updateCollectionFunctionGroup,
  closeCollectionFunctionGroupEditPage: actions.closeCollectionFunctionGroupEditPage,
  getCollectionFunctionGroup: actions.getCollectionFunctionGroup,
  getSecurityInfos: securityInfoActions.getSecurityInfos,
  getContacts: contactActions.getAllContacts,
};

const connector = connect(mapState, mapDispatch);

type PropsFromRedux = ConnectedProps<typeof connector>;
type PropsFromRouter = RouteComponentProps<RouterParams>

export type Props = PropsFromRedux & PropsFromRouter;

export const CollectionFunctionGroupEdit: FunctionComponent<Props> = ({
  isOpen,
  deliveryFunctionId,
  selectedCollectionFunctionGroup,
  history,
  isAuditEnabled,
  userId,
  userName,
  collectionFunctionGroup,
  securityInfos,
  contacts,
  editCollectionFunctionGroup,
  closeCollectionFunctionGroupEditPage,
  getCollectionFunctionGroup,
  getSecurityInfos,
  getContacts,
}: Props) => {
  const [template, setTemplate] = useState(collectionFunctionGroupTemplate);
  const [formData, setFormData] = useState<Partial<CollectionFunctionGroupFormData>>({});
  const collectionFunctionGroupFormRef = useRef<CollectionFunctionGroupFormRef>(null);
  const { t } = useTranslation();

  useEffect(() => {
    getSecurityInfos(deliveryFunctionId);
  }, [deliveryFunctionId, getSecurityInfos]);

  useEffect(() => {
    getContacts(deliveryFunctionId);
  }, [deliveryFunctionId, getContacts]);

  useEffect(() => {
    const id = selectedCollectionFunctionGroup[0]?.id;

    if (id) {
      getCollectionFunctionGroup(deliveryFunctionId, id);
    }
  }, [deliveryFunctionId, selectedCollectionFunctionGroup, getCollectionFunctionGroup]);

  useEffect(() => {
    if (collectionFunctionGroup != null) {
      setFormData(adaptToForm(collectionFunctionGroup));

      const clonedTemplate = cloneDeep(collectionFunctionGroupTemplate);
      clonedTemplate.formId = Date.now().toString();
      (clonedTemplate.main as FormTemplateSection).fields.hi1notifysecid.options = [
        { label: 'None', value: '' },
        ...securityInfos.map(({ name }) => ({ label: name, value: name })),
      ];
      (clonedTemplate.main as FormTemplateSection).fields.contact.options = [
        { label: 'None', value: '' },
        ...contacts.map(({ id, name }) => ({ label: name, value: id })),
      ];
      setTemplate(clonedTemplate);
    }
  }, [collectionFunctionGroup, securityInfos, contacts]);

  const handleFieldUpdate = ({ name: fieldName, value }: FieldUpdateHandlerParam) => {
    const clonedFormData = cloneDeep(formData);

    setFormData({
      ...clonedFormData,
      [fieldName]: value,
    });
  };

  const handleClickSave = () => {
    if (collectionFunctionGroup != null && collectionFunctionGroup.id == null) {
      return;
    }

    const isValid = collectionFunctionGroupFormRef?.current?.isValid(true, true) ?? true;
    if (!isValid) {
      return;
    }

    const formValues = collectionFunctionGroupFormRef?.current?.getValues();
    if (!formValues || collectionFunctionGroup == null) {
      return;
    }

    const body = getCollectionFunctionGroupDataToSubmit(
      formValues,
      collectionFunctionGroup,
      isAuditEnabled,
      userId,
      userName,
      'UPDATE',
    );

    editCollectionFunctionGroup(deliveryFunctionId, collectionFunctionGroup?.id, body, history);
  };

  const handleClickCancel = () => closeCollectionFunctionGroupEditPage(history);

  if (!collectionFunctionGroup) {
    return null;
  }

  const renderFormPanel = (formTitle: string) => {
    return (
      <FormPanel title={formTitle}>
        <FormButtons
          isEditing
          onClickCancel={handleClickCancel}
          onClickSave={handleClickSave}
          onClickClose={handleClickCancel}
        />
      </FormPanel>
    );
  };

  return (
    <div data-testid="CollectionFunctionGroupEdit">
      {renderFormPanel(t('collectionFunctionGroup.edit.title'))}
      <CollectionFunctionGroupForm
        data={formData}
        template={template}
        ref={collectionFunctionGroupFormRef}
        onFieldUpdate={handleFieldUpdate}
      />
    </div>
  );
};

export default withRouter(connector(CollectionFunctionGroupEdit));
